import React, { useState, useEffect, useRef } from 'react';
import { resetItems, resetVendors, resetCategories, resetBrands, resetInventory, resetUsers, downloadTemplate, importFile } from '../../lib/api';
import { downloadBlob } from '../../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import {
  Trash2, Upload, Download, Loader2, AlertTriangle, ShieldAlert,
  Package, Truck, Tags, Building2, Boxes, Users, FileSpreadsheet, Receipt,
  Phone, Mail, User, Eye, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESET_SECTIONS = [
  { key: 'items', label: 'Items', desc: 'All items and vendor pricing', icon: Package, fn: resetItems, color: 'text-blue-600' },
  { key: 'vendors', label: 'Vendors', desc: 'All vendors, contacts, and pricing', icon: Truck, fn: resetVendors, color: 'text-purple-600' },
  { key: 'categories', label: 'Categories', desc: 'All parent and subcategories', icon: Tags, fn: resetCategories, color: 'text-teal-600', note: 'Reset items first' },
  { key: 'brands', label: 'Brands', desc: 'All brand records', icon: Building2, fn: resetBrands, color: 'text-orange-600', note: 'Reset items first' },
  { key: 'inventory', label: 'Stock & Transactions', desc: 'All stock levels and transaction history', icon: Boxes, fn: resetInventory, color: 'text-red-600' },
  { key: 'users', label: 'Users', desc: 'All users except your admin account', icon: Users, fn: resetUsers, color: 'text-pink-600' },
];

const IMPORT_SECTIONS = [
  { key: 'categories', label: 'Categories', desc: 'Parent categories and subcategories', icon: Tags, template: 'categories' },
  { key: 'brands', label: 'Brands', desc: 'Brand names', icon: Building2, template: 'brands' },
  { key: 'vendors', label: 'Vendors', desc: 'Vendor names with contacts', icon: Truck, template: 'vendors' },
  { key: 'items', label: 'Items', desc: 'Items + optional vendor pricing (Sheet 2)', icon: Package, template: 'items', supportsOptions: true },
  { key: 'restock-invoice', label: 'Restock Invoice', desc: 'Import purchase invoice to restock', icon: Receipt, template: 'restock-invoice' },
];

const AdminContactSettings = () => {
  const [admins, setAdmins] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/auth/company-contact`)
      .then(res => { setAdmins(res.data.admins || []); setLoaded(true); })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loaded) return null;

  return (
    <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
      <CardHeader>
        <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
          <Phone size={20} className="text-[#0776BD]" /> Login Help Contact Info
        </CardTitle>
        <p className="text-sm text-[#6B7280]">This information is displayed on the Login Help page. It is automatically synced from active Admin users. To update, edit admin profiles in User Management.</p>
      </CardHeader>
      <CardContent>
        {admins.length > 0 ? (
          <div className="space-y-3">
            {admins.map((admin, idx) => (
              <div key={admin.email || `admin-${idx}`} className="flex items-start gap-3 p-3 bg-[#F5F6F7] rounded-lg" data-testid={`admin-contact-card-${idx}`}>
                <div className="p-2 bg-[#0776BD]/10 rounded-full mt-0.5">
                  <User size={16} className="text-[#0776BD]" />
                </div>
                <div className="space-y-0.5">
                  <p className="font-medium text-[#1F2937]">{admin.name}</p>
                  {admin.email && (
                    <p className="text-sm text-[#6B7280] flex items-center gap-1.5">
                      <Mail size={12} /> {admin.email}
                    </p>
                  )}
                  {admin.phone_number && (
                    <p className="text-sm text-[#6B7280] flex items-center gap-1.5">
                      <Phone size={12} /> {admin.phone_number}
                    </p>
                  )}
                  {!admin.phone_number && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      No phone number set
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#6B7280] text-center py-4">No active admin users found.</p>
        )}
      </CardContent>
    </Card>
  );
};

const DataManagement = () => {
  const [resetDialog, setResetDialog] = useState({ open: false, section: null });
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');

  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [pendingFile, setPendingFile] = useState({}); // {key: File} — held until user clicks Preview/Upload
  const fileInputRefs = useRef({}); // {key: HTMLInputElement}

  const handleReset = async () => {
    if (!resetPassword) { setResetError('Password is required'); return; }
    const section = resetDialog.section;
    setResetting(true);
    setResetError('');
    try {
      const res = await section.fn({ password: resetPassword });
      toast.success(res.data.message);
      setResetDialog({ open: false, section: null });
      setResetPassword('');
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleDownloadTemplate = async (templateKey) => {
    setDownloading(templateKey);
    try {
      const res = await downloadTemplate(templateKey);
      downloadBlob(res.data, `${templateKey}_template.xlsx`);
      toast.success(`${templateKey.replace('-', ' ')} template downloaded`);
    } catch (err) {
      toast.error('Failed to download template. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleImport = async (importKey, file, options = {}) => {
    setImporting(importKey);
    setImportResult(null);
    try {
      const res = await importFile(importKey, file, options);
      setImportResult({ key: importKey, ...res.data, preview: options.preview || false });
      toast.success(res.data.message);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Import failed';
      setImportResult({ key: importKey, message: detail, item_errors: [], pricing_errors: [], errors: [], failed: true });
      toast.error(detail);
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="space-y-8 animate-slide-in" data-testid="data-management-page">
      <div>
        <h1 className="heading-2 text-[#1F2937]">Data Management</h1>
        <p className="text-[#6B7280]">Import data from Excel or reset system data</p>
      </div>

      {/* IMPORT SECTION */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-[#0776BD]" />
            Bulk Import from Excel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#6B7280]">Download a template, fill in your data, then upload to import. Import in order: Categories &rarr; Brands &rarr; Vendors &rarr; Items &rarr; Restock Invoice.</p>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {IMPORT_SECTIONS.map((sec) => {
              const Icon = sec.icon;
              const isImporting = importing === sec.key;
              const result = importResult?.key === sec.key ? importResult : null;
              return (
                <div key={sec.key} className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`import-section-${sec.key}`}>
                  <div className="flex items-center gap-2">
                    <Icon size={18} className="text-[#0776BD]" />
                    <span className="font-semibold text-[#1F2937]">{sec.label}</span>
                  </div>
                  <p className="text-xs text-[#6B7280]">{sec.desc}</p>

                  {sec.supportsOptions && (
                    <label className="flex items-center gap-2 text-xs text-[#1F2937] cursor-pointer select-none" data-testid="update-existing-toggle">
                      <input
                        type="checkbox"
                        checked={updateExisting}
                        onChange={(e) => setUpdateExisting(e.target.checked)}
                        className="h-3.5 w-3.5 accent-[#0776BD]"
                      />
                      <RefreshCw size={12} className="text-[#6B7280]" />
                      Update existing on duplicates
                    </label>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleDownloadTemplate(sec.template)}
                      disabled={downloading === sec.template}
                      className="gap-1 text-xs rounded-lg border-[#0776BD] text-[#0776BD]"
                      data-testid={`download-template-${sec.key}`}
                    >
                      {downloading === sec.template ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {downloading === sec.template ? 'Downloading...' : 'Template'}
                    </Button>

                    {sec.supportsOptions ? (
                      <>
                        <input
                          ref={(el) => { fileInputRefs.current[sec.key] = el; }}
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) setPendingFile(prev => ({ ...prev, [sec.key]: f }));
                            e.target.value = '';
                          }}
                          disabled={isImporting}
                          data-testid={`upload-input-${sec.key}`}
                        />
                        <Button
                          variant="outline" size="sm"
                          type="button"
                          disabled={isImporting}
                          onClick={() => fileInputRefs.current[sec.key]?.click()}
                          className="gap-1 text-xs rounded-lg border-gray-300"
                          data-testid={`choose-file-${sec.key}`}
                        >
                          <FileSpreadsheet size={14} />
                          {pendingFile[sec.key] ? pendingFile[sec.key].name.slice(0, 18) : 'Choose file'}
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          type="button"
                          disabled={!pendingFile[sec.key] || isImporting}
                          onClick={() => handleImport(sec.key, pendingFile[sec.key], { preview: true, update_existing: updateExisting })}
                          className="gap-1 text-xs rounded-lg border-amber-400 text-amber-700 hover:bg-amber-50"
                          data-testid={`preview-btn-${sec.key}`}
                        >
                          <Eye size={14} /> Preview
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          disabled={!pendingFile[sec.key] || isImporting}
                          onClick={() => {
                            const f = pendingFile[sec.key];
                            handleImport(sec.key, f, { update_existing: updateExisting });
                            setPendingFile(prev => { const n = { ...prev }; delete n[sec.key]; return n; });
                          }}
                          className="gap-1 text-xs rounded-lg bg-[#0776BD] hover:bg-[#055A92]"
                          data-testid={`import-btn-${sec.key}`}
                        >
                          {isImporting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          {isImporting ? 'Importing...' : 'Import'}
                        </Button>
                      </>
                    ) : (
                      <label className="flex-1">
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) handleImport(sec.key, e.target.files[0]);
                            e.target.value = '';
                          }}
                          disabled={isImporting}
                          data-testid={`upload-input-${sec.key}`}
                        />
                        <Button
                          variant="default" size="sm" asChild
                          className="w-full gap-1 text-xs rounded-lg bg-[#0776BD] hover:bg-[#055A92] cursor-pointer"
                        >
                          <span>
                            {isImporting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            {isImporting ? 'Importing...' : 'Upload'}
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>

                  {result && (
                    <div className={`text-xs p-2 rounded-lg ${result.failed ? 'bg-red-50 text-red-700' : (result.preview ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700')}`}>
                      <p className="font-medium">{result.message}</p>
                      {(result.items_created !== undefined || result.pricing_created !== undefined) && (
                        <p className="mt-1 opacity-80">
                          Items: {result.items_created || 0} created, {result.items_updated || 0} updated
                          {result.pricing_created !== undefined && ` · Pricing: ${result.pricing_created} created, ${result.pricing_updated || 0} updated`}
                        </p>
                      )}
                      {(() => {
                        const errs = [
                          ...(result.item_errors || []).map(e => `[Items] ${e}`),
                          ...(result.pricing_errors || []).map(e => `[Pricing] ${e}`),
                          ...(result.errors || []),
                        ];
                        return errs.length > 0 ? (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-amber-700">{errs.length} warning(s)</summary>
                            <ul className="mt-1 space-y-0.5 text-amber-700">
                              {errs.map((e, i) => <li key={`err-${i}`}>{e}</li>)}
                            </ul>
                          </details>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* RESET SECTION */}
      <Card className="bg-white rounded-xl border border-red-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-red-700 flex items-center gap-2">
            <ShieldAlert size={20} />
            Reset Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-red-50 border-red-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700 text-sm">
              Reset actions permanently delete data and <strong>cannot be undone</strong>. Your admin password is required for each reset.
            </AlertDescription>
          </Alert>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {RESET_SECTIONS.map((sec) => {
              const Icon = sec.icon;
              return (
                <div key={sec.key} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`reset-section-${sec.key}`}>
                  <div className="flex items-center gap-3">
                    <Icon size={20} className={sec.color} />
                    <div>
                      <p className="font-semibold text-[#1F2937] text-sm">{sec.label}</p>
                      <p className="text-xs text-[#6B7280]">{sec.desc}</p>
                      {sec.note && <p className="text-xs text-amber-600">{sec.note}</p>}
                    </div>
                  </div>
                  <Button
                    variant="destructive" size="sm"
                    onClick={() => { setResetDialog({ open: true, section: sec }); setResetPassword(''); setResetError(''); }}
                    className="gap-1 rounded-lg shrink-0"
                    data-testid={`reset-btn-${sec.key}`}
                  >
                    <Trash2 size={14} /> Reset
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Admin Contact Info (for Login Help page) */}
      <AdminContactSettings />

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(v) => { if (!v) setResetDialog({ open: false, section: null }); }}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-700 flex items-center gap-2">
              <AlertTriangle size={22} /> Confirm Reset: {resetDialog.section?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="bg-red-50 border-red-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                This will permanently delete <strong>all {resetDialog.section?.label?.toLowerCase()}</strong> data. This action cannot be undone.
              </AlertDescription>
            </Alert>

            {resetError && (
              <Alert variant="destructive" className="rounded-lg">
                <AlertDescription>{resetError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Enter your admin password to confirm</Label>
              <Input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Admin password"
                className="h-11 rounded-lg border-gray-300"
                data-testid="reset-password-confirm"
                onKeyDown={(e) => e.key === 'Enter' && handleReset()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog({ open: false, section: null })} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleReset} disabled={resetting || !resetPassword} className="rounded-lg gap-1" data-testid="confirm-reset-btn">
              {resetting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagement;
